import { BoxIconLine, GroupIcon, TaskIcon, CalenderIcon } from "../../icons";
import { FiAlertTriangle, FiInfo } from "react-icons/fi";

interface EcommerceMetricsProps {
  totalStudents: number;
  totalWorkouts: number;
  totalAppointments: number;
  totalAvaliations: number;
  totalExpiredWorkouts: number;
}

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-2">
    <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
    <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden w-48 -translate-x-1/2 rounded bg-gray-800 p-2 text-center text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-50">
      {text}
      <div className="absolute left-1/2 top-full -ml-1 h-0 w-0 border-4 border-solid border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

export default function EcommerceMetrics({
  totalStudents,
  totalWorkouts,
  totalAppointments,
  totalAvaliations,
  totalExpiredWorkouts,
}: EcommerceMetricsProps) {
  const metrics = [
    {
      icon: <GroupIcon className="h-7 w-7 text-white" />,
      label: "Alunos Ativos",
      value: totalStudents,
      tooltip: "Total de alunos com matrícula ativa. Ex: 10 alunos.",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      iconBg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      shadowColor: "rgba(102, 126, 234, 0.5)",
    },
    {
      icon: <BoxIconLine className="h-7 w-7 text-white" />,
      label: "Treinos Realizados",
      value: totalWorkouts,
      tooltip: "Total de treinos concluídos pelos alunos. Ex: 45 treinos.",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      iconBg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      shadowColor: "rgba(79, 172, 254, 0.5)",
    },
    {
      icon: <CalenderIcon className="h-7 w-7 text-white" />,
      label: "Total Agendamentos",
      value: totalAppointments,
      tooltip: "Total de agendamentos marcados. Ex: 20 agendamentos.",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      iconBg: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      shadowColor: "rgba(67, 233, 123, 0.5)",
    },
    {
      icon: <TaskIcon className="h-7 w-7 text-white" />,
      label: "Avaliações",
      value: totalAvaliations,
      tooltip: "Total de avaliações físicas realizadas. Ex: 5 avaliações.",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      iconBg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      shadowColor: "rgba(240, 147, 251, 0.5)",
    },
    {
      icon: <FiAlertTriangle className="h-7 w-7 text-white" />,
      label: "Treinos Vencidos",
      value: totalExpiredWorkouts,
      tooltip: "Total de fichas de treino que precisam ser renovadas.",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      iconBg: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      shadowColor: "rgba(250, 112, 154, 0.5)",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-5 md:gap-6">
      {metrics.map((metric, index) => (
        <div
          key={index}
          className="group rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-3 shadow-sm transition-all duration-300 dark:border-gray-800 dark:from-white/[0.03] dark:to-white/[0.01] sm:p-4 md:rounded-2xl md:p-6 md:hover:shadow-xl"
        >
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110 sm:h-14 sm:w-14"
            style={{
              background: metric.iconBg,
              boxShadow: `0 10px 25px -5px ${metric.shadowColor}`,
            }}
          >
            {metric?.icon}
          </div>

          <div className="mt-3 flex items-end justify-between sm:mt-5">
            <div>
              <div className="flex items-center">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:text-xs">
                  {metric.label}
                </span>
                <Tooltip text={metric.tooltip} />
              </div>
              <h4
                className="mt-1.5 bg-clip-text text-2xl font-bold text-transparent sm:mt-2 sm:text-3xl"
                style={{ backgroundImage: metric.gradient }}
              >
                {metric.value}
              </h4>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
